import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Check, Plus, Package, Users, FileText, Settings, Eye, Upload, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AddAssetFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [roles, setRoles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    capacity: 12,
    fuelLevel: 'unknown',
    active: true,
    notes: '',
    photoUrl: '',
    teamRequirements: [], // [{roleId, roleName, quantity}]
    allowedTeamMembers: [],
    documents: [] // [{name, label, url, uploadedAt}]
  });

  const totalSteps = 5;

  useEffect(() => {
    fetchRoles();
    fetchTeamMembers();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles');
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/team');
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching team_members:', error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error('Please enter asset name');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        capacity: formData.capacity,
        fuelLevel: formData.fuelLevel === 'unknown' ? null : formData.fuelLevel,
        active: formData.active,
        notes: formData.notes?.trim() || null,
        photoUrl: formData.photoUrl || null,
        allowedTeamMembers: formData.allowedTeamMembers,
        teamRequirements: formData.teamRequirements,
        documents: formData.documents
      };

      await api.post('/assets', payload);
      toast.success('Asset added successfully!');
      navigate('/dashboard?tab=assets');
    } catch (error) {
      console.error('Error adding asset:', error);
      toast.error('Failed to add asset');
    }
  };

  const addTeamRequirement = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role && !formData.teamRequirements.find(req => req.roleId === roleId)) {
      const newRequirements = [...formData.teamRequirements, {
        roleId: role.id,
        roleName: role.name,
        quantity: 1
      }];
      updateFormData('teamRequirements', newRequirements);
    }
  };

  const updateTeamRequirement = (index, quantity) => {
    const updated = [...formData.teamRequirements];
    updated[index].quantity = parseInt(quantity) || 1;
    updateFormData('teamRequirements', updated);
  };

  const removeTeamRequirement = (index) => {
    const updated = formData.teamRequirements.filter((_, i) => i !== index);
    updateFormData('teamRequirements', updated);
  };

  const handleDocumentUpload = async (file, label) => {
    if (!file) return;
    
    setUploadingDocument(true);
    try {
      // Mock upload - in real app, upload to cloud storage
      const mockUrl = `https://example.com/documents/${file.name}`;
      
      const newDocument = {
        name: file.name,
        label: label || file.name,
        url: mockUrl,
        uploadedAt: new Date().toISOString()
      };
      
      const updatedDocuments = [...formData.documents, newDocument];
      updateFormData('documents', updatedDocuments);
      
      toast.success(`Document "${label || file.name}" uploaded successfully`);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const removeDocument = (index) => {
    const updated = formData.documents.filter((_, i) => i !== index);
    updateFormData('documents', updated);
    toast.success('Document removed');
  };

  const addNewTeam = () => {
    navigate('/team/add');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Asset Information</h2>
              <p className="text-gray-600 mt-2">Let's start with the basic asset details</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Asset Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Enter asset name (e.g., Sea Breeze, Ocean Dream)"
                  className="mt-1 h-12 text-lg"
                />
              </div>

              <div>
                <Label htmlFor="capacity" className="text-sm font-medium text-gray-700">Capacity</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  value={formData.capacity}
                  onChange={(e) => updateFormData('capacity', parseInt(e.target.value) || 12)}
                  placeholder="12"
                  className="mt-1 h-12 text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum capacity for this asset</p>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700">Current Status</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="active"
                      checked={formData.active === true}
                      onChange={() => updateFormData('active', true)}
                      className="w-4 h-4"
                    />
                    <span className="text-green-600 font-medium">Active & Available</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="active"
                      checked={formData.active === false}
                      onChange={() => updateFormData('active', false)}
                      className="w-4 h-4"
                    />
                    <span className="text-gray-600 font-medium">Inactive</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Team Requirements</h2>
              <p className="text-gray-600 mt-2">What team is needed to operate {formData.name || 'this asset'}?</p>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Current Requirements</h3>
                {formData.teamRequirements.length > 0 ? (
                  <div className="space-y-3">
                    {formData.teamRequirements.map((requirement, index) => (
                      <div key={index} className="flex items-center gap-3 bg-white p-3 rounded border">
                        <Badge 
                          style={{ 
                            backgroundColor: roles.find(r => r.id === requirement.roleId)?.color || '#3B82F6',
                            color: 'white'
                          }}
                        >
                          {requirement.roleName}
                        </Badge>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={requirement.quantity}
                          onChange={(e) => updateTeamRequirement(index, e.target.value)}
                          className="w-20 h-8"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeTeamRequirement(index)}
                          className="p-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-600 mb-2">⭐ No team requirements set</p>
                    <p className="text-sm text-gray-500">This asset can operate without team (self-service)</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">Add Team Role Requirement</Label>
                <Select onValueChange={addTeamRequirement}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role to add..." />
                  </SelectTrigger>
                  <SelectContent>
                    {roles
                      .filter(role => !formData.teamRequirements.find(req => req.roleId === role.id))
                      .map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: role.color || '#3B82F6' }}
                            />
                            <span>{role.name}</span>
                            {role.description && (
                              <span className="text-xs text-gray-500">- {role.description}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-center pt-4">
                <p className="text-xs text-gray-500 mb-3">
                  Need a role that's not listed?
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard?tab=roles&action=add')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add New Role
                </Button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Allowed Team</h2>
              <p className="text-gray-600 mt-2">Which teamMembers can operate {formData.name || 'this asset'}?</p>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-4">
                  Leave empty to allow all teamMembers, or select specific team who are authorized to operate this asset.
                </p>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {teamMembers.map((teamMember) => (
                    <label key={teamMember.id} className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allowedTeamMembers.includes(teamMember.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateFormData('allowedTeamMembers', [...formData.allowedTeamMembers, teamMember.id]);
                          } else {
                            updateFormData('allowedTeamMembers', formData.allowedTeamMembers.filter(id => id !== teamMember.id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{teamMember.name}</span>
                          {teamMember.roleName && (
                            <Badge 
                              className="text-xs"
                              style={{ 
                                backgroundColor: roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6',
                                color: 'white'
                              }}
                            >
                              {teamMember.roleName}
                            </Badge>
                          )}
                        </div>
                        {teamMember.email && (
                          <p className="text-xs text-gray-500">{teamMember.email}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="text-center pt-4">
                <p className="text-xs text-gray-500 mb-3">
                  Need to add a new teamMember?
                </p>
                <Button
                  variant="outline"
                  onClick={addNewTeam}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add New TeamMember
                </Button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Asset Documents</h2>
              <p className="text-gray-600 mt-2">Upload important documents for {formData.name || 'this asset'} (optional)</p>
            </div>

            <div className="space-y-4">
              {/* Current Documents */}
              {formData.documents.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Uploaded Documents</h3>
                  <div className="space-y-2">
                    {formData.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded border">
                        <div>
                          <p className="font-medium text-gray-900">{doc.label}</p>
                          <p className="text-xs text-gray-500">{doc.name}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeDocument(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Document Upload */}
              <DocumentUploader onUpload={handleDocumentUpload} uploading={uploadingDocument} />
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Common Document Types</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>• Registration/Certificate of Documentation</p>
                  <p>• Insurance Certificate</p>
                  <p>• Safety Inspection Certificate</p>
                  <p>• Minimum Team Requirements</p>
                  <p>• Engineering/Maintenance Records</p>
                  <p>• Operating Manual</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-gray-600 mt-2">Please review the asset details before adding</p>
            </div>

            <Card className="border">
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="pb-4 border-b">
                    <h3 className="text-lg font-semibold mb-4">Asset Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-semibold">{formData.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Capacity</p>
                        <p className="font-semibold">Capacity: {formData.capacity}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <Badge className={formData.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {formData.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Team Requirements */}
                  <div className="pb-4 border-b">
                    <h3 className="text-lg font-semibold mb-4">Team Requirements</h3>
                    {formData.teamRequirements.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formData.teamRequirements.map((req, index) => (
                          <Badge 
                            key={index}
                            style={{ 
                              backgroundColor: roles.find(r => r.id === req.roleId)?.color || '#3B82F6',
                              color: 'white'
                            }}
                          >
                            {req.quantity} {req.roleName}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-600">⭐ No team required (self-service)</p>
                    )}
                  </div>

                  {/* Allowed Team */}
                  <div className="pb-4 border-b">
                    <h3 className="text-lg font-semibold mb-4">Allowed Team</h3>
                    {formData.allowedTeamMembers.length > 0 ? (
                      <p className="text-gray-600">{formData.allowedTeamMembers.length} specific teamMembers authorized</p>
                    ) : (
                      <p className="text-gray-600">All teamMembers allowed</p>
                    )}
                  </div>

                  {/* Documents */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Documents</h3>
                    {formData.documents.length > 0 ? (
                      <p className="text-gray-600">{formData.documents.length} document(s) uploaded</p>
                    ) : (
                      <p className="text-gray-600">No documents uploaded</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard?tab=assets')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Add New Asset</h1>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center gap-2">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${
                    i + 1 <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-8 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>

          {currentStep === totalSteps ? (
            <Button
              onClick={handleSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Add Asset
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Document Uploader Component
const DocumentUploader = ({ onUpload, uploading }) => {
  const [label, setLabel] = useState('');
  const [file, setFile] = useState(null);

  const handleUpload = () => {
    if (file) {
      onUpload(file, label || file.name);
      setFile(null);
      setLabel('');
    }
  };

  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-6">
      <div className="text-center mb-4">
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">Upload a document</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="docLabel" className="text-sm font-medium text-gray-700">Document Label</Label>
          <Input
            id="docLabel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g., Insurance Certificate, Registration"
            className="mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="docFile" className="text-sm font-medium text-gray-700">Choose File</Label>
          <input
            id="docFile"
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
        </div>
        
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full"
        >
          {uploading ? 'Uploading...' : 'Upload Document'}
        </Button>
      </div>
    </div>
  );
};

export default AddAssetFlow;